import React, { useEffect, useState, useRef, Children } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { PhoneIcon, ChevronDownIcon, StarIcon, ShieldCheckIcon, ClockIcon, CalendarIcon, ArrowRightIcon, MessageSquareIcon } from 'lucide-react';
import { FallingLeaves } from './FallingLeaves';
// Floating particle component
function Particle({
  style
}: {
  style: React.CSSProperties;
}) {
  return <div className="absolute rounded-full pointer-events-none" style={style} data-id="element-174" />;
}
const PARTICLES = [{
  width: 4,
  height: 4,
  top: '15%',
  left: '8%',
  background: 'rgba(11,122,196,0.6)',
  animationDelay: '0s',
  animationDuration: '7s'
}, {
  width: 6,
  height: 6,
  top: '25%',
  left: '18%',
  background: 'rgba(122,201,67,0.5)',
  animationDelay: '1s',
  animationDuration: '9s'
}, {
  width: 3,
  height: 3,
  top: '60%',
  left: '5%',
  background: 'rgba(255,212,0,0.5)',
  animationDelay: '2s',
  animationDuration: '6s'
}, {
  width: 5,
  height: 5,
  top: '40%',
  left: '90%',
  background: 'rgba(11,122,196,0.5)',
  animationDelay: '0.5s',
  animationDuration: '8s'
}, {
  width: 4,
  height: 4,
  top: '70%',
  left: '85%',
  background: 'rgba(125,211,252,0.6)',
  animationDelay: '3s',
  animationDuration: '10s'
}, {
  width: 7,
  height: 7,
  top: '80%',
  left: '75%',
  background: 'rgba(249,115,22,0.4)',
  animationDelay: '1.5s',
  animationDuration: '7s'
}, {
  width: 3,
  height: 3,
  top: '20%',
  left: '75%',
  background: 'rgba(255,212,0,0.4)',
  animationDelay: '4s',
  animationDuration: '11s'
}, {
  width: 5,
  height: 5,
  top: '50%',
  left: '50%',
  background: 'rgba(122,201,67,0.3)',
  animationDelay: '2.5s',
  animationDuration: '9s'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.18,
      delayChildren: 0.4
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 28
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.85,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
const wordVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      delay: 0.6 + i * 0.08,
      ease: [0.22, 1, 0.36, 1]
    }
  })
};
export function Hero() {
  const {
    scrollY
  } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 320]);
  const overlayOpacity = useTransform(scrollY, [0, 600], [0.72, 0.92]);
  const contentOpacity = useTransform(scrollY, [0, 400], [1, 0]);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  const headlineWords = ['Professional', 'Gutter', 'Cleaning', 'That'];
  const accentWords = ['Protects', 'Your', 'Home'];
  return <section id="home" className="relative h-screen min-h-[680px] flex items-center justify-center overflow-hidden bg-[#0A1628]" data-id="element-175">
      {/* Parallax Background */}
      <motion.div style={{
      y
    }} className="absolute inset-0 w-full h-[125%] -top-[12%]" data-id="element-176">
        <img src="https://cdn.magicpatterns.com/uploads/bXYNayw9kimv9hxuqh5jqZ/ChatGPT_Image_Mar_13,_2026,_04_44_16_PM.png" alt="NJ Four Seasons Gutter Cleaning technician working on a roof" className="absolute inset-0 w-full h-full object-cover object-center" data-id="element-177" />
      </motion.div>

      {/* Layered gradient overlay */}
      <motion.div style={{
      opacity: overlayOpacity,
      background: 'linear-gradient(160deg, rgba(10,22,40,0.85) 0%, rgba(10,22,40,0.65) 50%, rgba(10,22,40,0.88) 100%)',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none'
    }} data-id="element-178" />

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-[#0A1628] to-transparent pointer-events-none z-10" data-id="element-179" />

      {/* Falling autumn leaves — seasonal brand element */}
      <FallingLeaves count={isMobile ? 4 : 8} data-id="element-180" />

      {/* Floating particles */}
      {PARTICLES.map((p, i) => <Particle key={i} style={{
      width: p.width,
      height: p.height,
      top: p.top,
      left: p.left,
      background: p.background,
      animationDelay: p.animationDelay,
      animationDuration: p.animationDuration,
      animation: `particle-drift ${p.animationDuration} ease-in-out ${p.animationDelay} infinite`
    }} data-id="element-181" />)}

      {/* Content */}
      <motion.div style={{
      opacity: contentOpacity
    }} className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-24 pb-32" data-id="element-182">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="max-w-4xl mx-auto text-center" data-id="element-183">
          {/* Badge */}
          <motion.div variants={itemVariants} className="mb-6 inline-flex items-center gap-2.5 px-4 py-1.5 rounded-md glass-dark border border-white/10" data-id="element-184">
            <span className="flex h-2 w-2 rounded-full bg-[#7AC943]" data-id="element-185">
              <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-[#7AC943] opacity-75" data-id="element-186" />
            </span>
            <span className="text-xs font-bold text-gray-200 uppercase tracking-[0.2em]" data-id="element-187">
              Paterson, NJ — Serving All of North Jersey
            </span>
          </motion.div>

          {/* Headline with word-by-word animation */}
          <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-4 text-shadow-xl leading-[1.1] tracking-tight uppercase" data-id="element-188">
            <span className="block" data-id="element-189">
              {headlineWords.map((word, i) => <motion.span key={word} custom={i} variants={wordVariants} initial="hidden" animate="visible" className="inline-block mr-3" data-id="element-190">
                  {word}
                </motion.span>)}
            </span>
            <span className="block mt-1" data-id="element-191">
              {accentWords.map((word, i) => <motion.span key={word} custom={headlineWords.length + i} variants={wordVariants} initial="hidden" animate="visible" className={`inline-block mr-3 ${word === 'Protects' ? 'text-[#0B7AC4]' : word === 'Home' ? 'text-[#FFD400]' : 'text-white'}`} data-id="element-192">
                  {word}
                </motion.span>)}
            </span>
          </h1>

          {/* Subheadline */}
          <motion.p variants={itemVariants} className="text-xl md:text-2xl text-gray-300 mb-8 text-shadow-md max-w-2xl mx-auto font-light leading-relaxed" data-id="element-193">
            Trusted gutter cleaning, repair, and installation services across
            New Jersey.{' '}
            <span className="text-[#7AC943] font-semibold" data-id="element-194">
              Free estimates.
            </span>
          </motion.p>

          {/* CTA Buttons */}
          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-195">
            <a href="#contact" className="btn-primary btn-ripple w-full sm:w-auto text-lg flex items-center justify-center gap-2 group" data-id="element-196">
              Text For Free Estimate
              <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-197" />
            </a>
            <a href="tel:8624072386" className="btn-yellow btn-ripple w-full sm:w-auto text-lg flex items-center justify-center gap-2" data-id="element-198">
              <PhoneIcon className="w-5 h-5" data-id="element-199" />
              Call (862) 407-2386
            </a>
          </motion.div>

          {/* Mobile-only SMS CTA */}
          <motion.div variants={itemVariants} className="mt-4 md:hidden" data-id="element-200">
            <a href="sms:8624072386?body=Hi,%20I%20need%20a%20free%20gutter%20estimate.%20My%20name%20is%20___%20and%20my%20address%20is%20___." className="inline-flex items-center justify-center gap-2 bg-[#7AC943] text-white px-6 py-3 rounded-full font-black text-base w-full sm:w-auto transition-all duration-300 hover:-translate-y-0.5" style={{
            boxShadow: '0 4px 15px rgba(122,201,67,0.35)'
          }} data-id="element-201">
              <MessageSquareIcon className="w-5 h-5" data-id="element-202" />
              Text Us for a Quote
            </a>
          </motion.div>

          {/* Promo note */}
          <motion.p variants={itemVariants} className="mt-4 text-sm text-[#FFD400] font-semibold tracking-wide" data-id="element-203">
            🔥 First-time customers save $15 — mention this offer when you call
          </motion.p>
        </motion.div>
      </motion.div>

      {/* Floating Stats Bar */}
      <motion.div initial={{
      y: 80,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      delay: 1.4,
      duration: 0.9,
      ease: [0.22, 1, 0.36, 1]
    }} className="absolute bottom-0 left-0 w-full z-30" data-id="element-204">
        <div className="max-w-5xl mx-auto px-4 pb-6" data-id="element-205">
          <div className="glass-dark rounded-xl px-6 py-5 flex flex-wrap justify-between items-center gap-4 divide-x divide-white/10 border border-white/20 shadow-2xl" data-id="element-206">
            {[{
            icon: StarIcon,
            value: '4.8★',
            label: '56+ Reviews',
            color: 'text-[#FFD400]',
            fill: true
          }, {
            icon: CalendarIcon,
            value: '10+',
            label: 'Years Experience',
            color: 'text-[#0B7AC4]',
            fill: false
          }, {
            icon: ShieldCheckIcon,
            value: '✓',
            label: 'Licensed & Insured',
            color: 'text-[#7AC943]',
            fill: false
          }, {
            icon: ClockIcon,
            value: 'Fast',
            label: 'Response Times',
            color: 'text-[#F97316]',
            fill: false
          }].map((stat, i) => <div key={i} className="flex-1 min-w-[120px] flex flex-col items-center justify-center px-4" data-id="element-207">
                <stat.icon className={`w-5 h-5 ${stat.color} mb-1 ${stat.fill ? 'fill-current' : ''}`} data-id="element-208" />
                <span className={`font-bold text-xl text-white uppercase tracking-wide`} data-id="element-209">
                  {stat.value}
                </span>
                <span className="text-gray-400 text-[10px] font-bold text-center uppercase tracking-widest mt-0.5" data-id="element-210">
                  {stat.label}
                </span>
              </div>)}
          </div>
        </div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 2
    }} className="absolute bottom-32 left-1/2 -translate-x-1/2 z-20 hidden lg:block" data-id="element-211">
        <motion.div animate={{
        y: [0, 8, 0]
      }} transition={{
        duration: 1.6,
        repeat: Infinity
      }} data-id="element-212">
          <ChevronDownIcon className="w-7 h-7 text-white/30" data-id="element-213" />
        </motion.div>
      </motion.div>
    </section>;
}